import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import moment from 'moment-timezone';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import PushPinIcon from '@mui/icons-material/PushPin';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CircleIcon from '@mui/icons-material/Circle';
import { useSidebarWidth } from '../Utility/useSidebarWidth'; // ← نفس import الـ ChannelTable

const C = {
  bg:        '#F7F8FC',
  white:     '#FFFFFF',
  border:    '#E8EAF0',
  accent:    '#0D47A1',
  text:      '#1C2340',
  textMuted: '#7B829A',
  success:   '#12B76A',
  warning:   '#F79009',
  shadow:    '0 1px 3px rgba(16,24,40,0.08)',
  shadowMd:  '0 4px 16px rgba(16,24,40,0.10)',
  iconBg:    '#EEF2FF',
};

const InfoRow = ({ icon: Icon, label, value, extra }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
    <div style={{
      width: 36, height: 36, borderRadius: 9, background: C.iconBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon sx={{ fontSize: 18, color: C.accent }} />
    </div>
    <div>
      <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif", marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: "'DM Sans', sans-serif" }}>{value}</span>
        {extra}
      </div>
    </div>
  </div>
);

const SectionCard = ({ title, icon: Icon, children }) => (
  <div style={{
    background: C.white, border: `1px solid ${C.border}`,
    borderRadius: 14, padding: '20px 22px',
    boxShadow: C.shadow, flex: 1,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: C.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon sx={{ fontSize: 18, color: C.accent }} />
      </div>
      <span style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: "'DM Sans', sans-serif" }}>{title}</span>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {children}
    </div>
  </div>
);

const StatItem = ({ value, label, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: 22, fontWeight: 800, color: color || C.accent, fontFamily: "'DM Sans', sans-serif", marginBottom: 4 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>{label}</div>
  </div>
);

export const MyAccountPage = () => {
  const theme      = useTheme();
  const mode       = theme.palette.mode;
  const sidebarW   = useSidebarWidth(); // ← نفس الـ ChannelTable
  const [user, setUser] = useState(null);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUser(JSON.parse(u));
  }, []);

  const getCleanImageUrl = (url) => {
    if (!url) return null;
    const parts = url.split('/UsersImages/');
    return parts.length > 2 ? `${parts[0]}/UsersImages/${parts[2]}` : url;
  };

  const profileImage = user?.data?.profileImg ? getCleanImageUrl(user.data.profileImg) : null;

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  if (!user) return (
    <div style={{
      position: 'fixed', top: 50, left: sidebarW, right: 0, bottom: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: C.bg, transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
    }}>
      <div style={{ fontSize: 14, color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>Loading...</div>
    </div>
  );

  const isActive = user?.data?.active;

  return (
    // ← نفس بالظبط pattern الـ ChannelTable
    <div style={{
      position: 'fixed',
      top: 50,
      left: sidebarW,
      right: 0,
      bottom: 0,
      transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
      overflowY: 'auto',
      background: C.bg,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ padding: '32px 28px 48px', maxWidth: 900 }}>

        {/* ── Page title ── */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.text, letterSpacing: '-0.02em' }}>My Account</div>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 4 }}>Manage your personal information and account details</div>
        </div>

        {/* ── Main card ── */}
        <div style={{
          background: C.white, border: `1px solid ${C.border}`,
          borderRadius: 16, boxShadow: C.shadowMd,
          padding: '28px 28px 0', overflow: 'hidden',
        }}>

          {/* Profile header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 22, marginBottom: 28, flexWrap: 'wrap' }}>
            {/* Avatar */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                alt={user?.data?.name || 'User'}
                src={profileImage}
                sx={{
                  width: 90, height: 90,
                  borderRadius: '16px',
                  bgcolor: C.accent,
                  border: `3px solid ${C.white}`,
                  boxShadow: C.shadowMd,
                }}
              >
                {!profileImage && (
                  <span style={{ fontSize: '2rem', fontWeight: 800, color: '#fff', fontFamily: "'DM Sans', sans-serif" }}>
                    {getInitials(user?.data?.name)}
                  </span>
                )}
              </Avatar>
              <div style={{
                position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)',
                background: isActive ? C.success : '#9e9e9e',
                color: '#fff', fontSize: 10, fontWeight: 700,
                padding: '2px 10px', borderRadius: 20,
                border: `2px solid ${C.white}`,
                letterSpacing: '0.05em', whiteSpace: 'nowrap',
                fontFamily: "'DM Sans', sans-serif",
              }}>
                {isActive ? '● ACTIVE' : '● INACTIVE'}
              </div>
            </div>

            {/* Name + role */}
            <div style={{ paddingTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <PersonIcon sx={{ fontSize: 16, color: C.textMuted }} />
                  <span style={{ fontSize: 20, fontWeight: 800, color: C.text }}>{user?.data?.name || 'User'}</span>
                </div>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '3px 12px', borderRadius: 20,
                  border: `1px solid ${C.warning}40`,
                  background: `${C.warning}12`,
                  fontSize: 12, fontWeight: 600, color: C.warning,
                }}>
                  <PushPinIcon sx={{ fontSize: 12 }} />
                  {user?.data?.role || 'No Role'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.textMuted }}>
                <CircleIcon sx={{ fontSize: 8, color: isActive ? C.success : '#9e9e9e' }} />
                Member since {moment(user?.data?.createdAt).format('MMMM YYYY')}
              </div>
            </div>
          </div>

          {/* Info cards */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
            <SectionCard title="Contact Information" icon={PhoneAndroidIcon}>
              <InfoRow icon={PhoneAndroidIcon} label="Phone Number" value={user?.data?.phone || 'Not provided'} />
              <InfoRow icon={AlternateEmailIcon} label="Email Address" value={user?.data?.email || 'Not provided'} />
            </SectionCard>

            <SectionCard title="General Information" icon={CalendarMonthIcon}>
              <InfoRow
                icon={AutorenewIcon}
                label="Account Status"
                value={isActive ? 'Active' : 'Inactive'}
                extra={
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                    background: isActive ? `${C.success}18` : '#f4433618',
                    color: isActive ? C.success : '#f44336',
                    border: `1px solid ${isActive ? C.success : '#f44336'}30`,
                  }}>
                    {isActive ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                }
              />
              <InfoRow icon={CalendarMonthIcon} label="Created Date"
                value={user?.data?.createdAt ? moment(user.data.createdAt).format('DD MMMM YYYY - h:mm A') : 'Not available'}
              />
              <InfoRow icon={AutorenewIcon} label="Last Updated"
                value={user?.data?.updatedAt ? moment(user.data.updatedAt).format('DD MMMM YYYY - h:mm A') : 'Not available'}
              />
            </SectionCard>
          </div>

          {/* Stats footer */}
          <div style={{
            borderTop: `1px solid ${C.border}`,
            padding: '20px 4px 24px',
            display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 16,
          }}>
            <StatItem value={moment(user?.data?.createdAt).fromNow(true)} label="Time as member" color={C.accent} />
            <StatItem value={isActive ? 'Active' : 'Inactive'} label="Current Status" color={isActive ? C.success : '#9e9e9e'} />
            <StatItem value={user?.data?.role || 'User'} label="User Role" color={C.text} />
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#bbb', fontStyle: 'italic' }}>
          Information last updated on {moment().format('MMMM DD, YYYY')}
        </div>

      </div>
    </div>
  );
};